import { Button, Card, Skeleton } from 'antd';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import React, { useEffect, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';

import CourseReviewCard from './CourseReviewCard';
import CreateCourseReview from './CreateCourseReview';
import CourseHeading from '../../BookLandingPageComp/landingPage/heading/CourseHeading';
import ButtonDefault, { ButtonVariants } from '../../shared/basic/button';
import { ShouldRender } from '../../shared/basic/ShouldRender';
import { formateErrorObject } from '../../shared/formateErrorObject';
import { notify } from '../../shared/notify';
import { tokenUpdate } from '../../../atoms/authModalControl';
import { totalReviews } from '../../../atoms/totalReviews';
import { user } from '../../../atoms/user';
import {
  checkIsPurchased,
  fetchUserProductReview,
  getAllReviews,
} from '../../../services/ReviewLandingPage';
import { PaginationProps } from '../../../utils/interfaces/Orders';
import { User } from '../../../utils/interfaces/User';

export interface IReviewsData {
  user: {
    zl_uid: string;
    name: string;
    avatar: string;
  };
  rating: number;
  title: string;
  text: string;
  createdAt: string;
  productId?: string;
  _id: string;
  likes: string[];
  dislikes: string[];
}

interface Props {
  isLoggedIn: boolean;
  productId: string;
  reviewOverview?: string;
  courseImage: string;
  courseName: string;
  authorName: string;
}

const CourseReviewSection: React.FC<Props> = ({
  productId,
  isLoggedIn,
  reviewOverview,
  courseImage,
  courseName,
  authorName,
}) => {
  const [loading, setLoading] = useState({ reviews: false });

  const [, setTotalReview] = useRecoilState(totalReviews);
  const [isTokenChanged, setIsTokenChanged] = useRecoilState(tokenUpdate);

  const [createModal, setCreateModal] = useState<{
    value: boolean;
    type: 'create' | 'edit';
  }>({ type: 'create', value: false });

  const [data, setData] = useState({
    reviews: [] as IReviewsData[],
  });

  const [pagination, setPagination] = useState({
    reviews: {} as PaginationProps,
  });

  const [page, setPage] = useState({ reviews: 1 });

  const [isPurchased, setIsPurchased] = useState(false);
  const [isReviewed, setIsReviewed] = useState<IReviewsData>();

  const userData: User | undefined = useRecoilValue(user);
  const screens = useBreakpoint();

  const reviewsLimit = 5;

  const fetchData = async (pageNum: number) => {
    try {
      setLoading((prev) => ({ ...prev, reviews: true }));

      const response = await getAllReviews({
        page: pageNum,
        limit: reviewsLimit,
        product_id: productId as string,
        ...(userData?.zl_uid && { zl_uid: userData.zl_uid }),
        type: 'course',
      });

      let userReview;
      if (userData?.zl_uid && productId) {
        userReview = await fetchUserProductReview({
          productId: productId as string,
          zl_uid: userData?.zl_uid,
        });
      }

      const formattedData = response?.reviews.map((review: IReviewsData) => ({
        ...review,
        user: { ...review.user },
      }));

      if (userReview?.reviews?.length) {
        setIsReviewed(userReview?.reviews[0]);
      }

      const updatedData =
        userReview && pageNum === 1
          ? [...(userReview?.reviews || []), ...formattedData]
          : formattedData;

      setPagination({
        reviews: {
          current_page: response?.meta?.currentPage,
          per_page: response?.meta?.limit,
          total: response?.meta?.totalPages,
        },
      });

      setTotalReview(
        response?.meta?.totalRecords + (userReview?.reviews?.length || 0),
      );

      setData({
        reviews:
          pageNum === 1 ? updatedData : [...data.reviews, ...updatedData],
      });
    } catch (error: any) {
      notify(formateErrorObject(error), 'error');
    } finally {
      setLoading((prev) => ({ ...prev, reviews: false }));
    }
  };

  useEffect(() => {
    if (productId) fetchData(page.reviews);
  }, [productId, page.reviews, userData]);

  const checkIfPurchased = async () => {
    try {
      const data = await checkIsPurchased(`${productId}` as string);
      setIsPurchased(data);
    } catch (error: any) {
      // notify(formateErrorObject(error), 'error');
    }
  };

  useEffect(() => {
    if (userData && productId) {
      checkIfPurchased();
    }
  }, [userData, productId]);

  const handlePagination = () => {
    if (pagination.reviews?.total && page.reviews < pagination.reviews.total) {
      setPage((prev) => ({ ...prev, reviews: prev.reviews + 1 }));
    }
  };

  useEffect(() => {
    if (isTokenChanged) {
      setIsTokenChanged(false);
      setPage({ reviews: 1 });
      setData({ reviews: [] });

      if (productId) {
        fetchData(1);
      }
    }
  }, [isTokenChanged]);

  return (
    <div id='review-container' className=''>
      <CourseHeading heading='Review Overview' />
      <div className='text-white text-xs 2xl:text-sm whitespace-pre-line '>
        {reviewOverview}
      </div>
      <div className='text-[#FFFFFF99] font-semibold text-[10px] md:text-xs mt-2 pb-[40px]'>
        AI-generated from the text of customer reviews
      </div>
      <div className='flex justify-between mb-5 md:mb-2 items-center'>
        <ShouldRender
          check={(isPurchased && userData) || data?.reviews?.length}
        >
          <div className='font-semibold'>Top Reviews from Verified Users</div>
        </ShouldRender>
      </div>

      <div className='mb-3 mt-6'>
        <ShouldRender check={userData && isPurchased}>
          <Button
            className='w-full bg-transparent text-white text-xs hover:border-[#3D3D3D]'
            size='large'
            onClick={() =>
              setCreateModal({
                type: isReviewed ? 'edit' : 'create',
                value: true,
              })
            }
          >
            {isReviewed ? 'Edit your review' : 'Write a Review'}
          </Button>
        </ShouldRender>
      </div>

      <div className='flex gap-y-3 flex-col'>
        {!loading.reviews
          ? data?.reviews?.map((review, index) => (
              <span key={`${review?._id}-${index}`}>
                <CourseReviewCard data={review} userId={userData?.zl_uid} />
              </span>
            ))
          : Array.from({ length: 2 }).map((_, index) => (
              <Card key={`card-${index}`} className='mb-2 md:mb-[16px]'>
                <div className='flex justify-between items-center w-full'>
                  <span className='flex items-center gap-x-4'>
                    <Skeleton.Avatar active size='large' shape='circle' />
                    <Skeleton.Input
                      style={{ width: 150 }}
                      active
                      size='small'
                    />
                  </span>
                  <ShouldRender check={screens.md}>
                    <Skeleton.Input style={{ width: 20 }} active size='small' />
                  </ShouldRender>
                </div>
                <Skeleton.Input
                  style={{ width: '100%', marginTop: 12 }}
                  active
                  size='default'
                />
              </Card>
            ))}
      </div>

      <ShouldRender check={page.reviews < pagination.reviews.total}>
        <ButtonDefault
          size={4}
          variant={ButtonVariants.GRAY}
          onClick={handlePagination}
          className='w-full mt-2.5'
        >
          <span className='flex text-xs gap-x-2 px-4 py-1 w-full justify-center whitespace-nowrap'>
            SHOW MORE
          </span>
        </ButtonDefault>
      </ShouldRender>

      <CreateCourseReview
        handleClose={() =>
          setCreateModal({
            value: false,
            type: 'create',
          })
        }
        authorName={authorName}
        courseImage={courseImage}
        courseName={courseName}
        isPurchased={isPurchased}
        type={createModal.type}
        isLoggedIn={isLoggedIn}
        isModalOpen={createModal.value}
        review={isReviewed}
        fetchData={async () => {
          await fetchData(1);
        }}
        productId={productId}
      />
    </div>
  );
};

export default CourseReviewSection;
